/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { StatTile, TrendChart, BarChartW, DonutW, TableW, GaugeW, DotMatrixW, HeatmapW, CompareW } from "./Charts";
import { CHART_TYPES, ACCENTS, accentById } from "./widgetTypes";
import { IcDots, IcGrip, IcAlert, IcEdit, IcCopy, IcTrash, IcDashboard } from "./icons";
import { Skeleton } from "../../../components/shared/Loading";

/**
 * A single dashboard widget: metric label + title, a ••• menu (edit / change
 * chart type / duplicate / remove), and the chart body for its type.
 * The header is the drag handle (class "wc-drag") used by react-grid-layout.
 */
export default function WidgetCard({
  widget,
  metric,
  series,
  summary,
  loading,
  editing,
  api,
  onChangeType,
  onEdit,
  onDuplicate,
  onCopyToDashboard,
  onRemove,
}) {
  const [menuAt, setMenuAt] = useState(null); // {top,left} | null
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!menuAt) return undefined;
    const onDown = (e) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setMenuAt(null);
      }
    };
    // Fixed menu pinned to the button's open-time coords: close on any scroll
    // (capture:true also catches inner scrollers) or resize so it can't strand.
    const close = () => setMenuAt(null);
    const onKey = (e) => { if (e.key === "Escape") setMenuAt(null); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menuAt]);

  const openMenu = (e) => {
    e.stopPropagation();
    const r = btnRef.current.getBoundingClientRect();
    // ~240px wide (two-column type grid); keep it on screen.
    setMenuAt({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left - 200, window.innerWidth - 260)) });
  };

  const title = widget.title || metric?.title || "Metric";
  const type = widget.chartType || "stat";
  // Per-card accent: scoping --db-brand here recolors the charts only —
  // card chrome (text, borders) stays quiet.
  const accent = accentById(widget.options?.accent);
  const accentVars = accent.c
    ? { "--db-brand": accent.c, "--db-brand-2": accent.c2, "--db-brand-soft": accent.soft }
    : undefined;
  // Optional soft gradient fill — the accent's tint fading into white.
  const tinted = widget.options?.fill === "tint";
  const tintVars = tinted
    ? { "--wc-tint": accent.soft || "var(--db-brand-soft)" }
    : undefined;
  // Only show the small uppercase eyebrow when the card has a custom title that
  // differs from the metric name — otherwise it just repeats the title.
  const eyebrow = widget.title && metric && widget.title !== metric.title ? metric.title : "";

  const renderBody = () => {
    // Cold load AND every later refetch (period/date-range change): never
    // draw a chart off numbers that are mid-flight, config already resolved
    // or not — a stat tile holding last period's total while the new one
    // loads reads as this period's answer.
    if (loading) {
      return (
        <div className="wc-loading">
          <Skeleton variant="rect" height={72} radius={8} />
        </div>
      );
    }
    if (!metric) {
      return (
        <div className="wc-empty">
          <IcAlert />
          <span>Metric unavailable</span>
        </div>
      );
    }
    if (type === "stat") return <StatTile metric={metric} summary={summary} series={series} graphPos={widget.options?.graph || "bottom"} />;
    if (type === "line") return <TrendChart metric={metric} series={series} area={false} />;
    if (type === "area") return <TrendChart metric={metric} series={series} area />;
    if (type === "bar") return <BarChartW metric={metric} series={series} />;
    if (type === "donut") return <DonutW metric={metric} summary={summary} />;
    if (type === "gauge") return <GaugeW metric={metric} summary={summary} />;
    if (type === "dots") return <DotMatrixW metric={metric} series={series} />;
    if (type === "heatmap") return <HeatmapW metric={metric} series={series} />;
    if (type === "compare") {
      // Main metric + up to 3 companions, each on its own accent line.
      const extraIds = (widget.options?.metrics || []).filter((id) => id !== widget.metric).slice(0, 3);
      const usedAccent = accentById(widget.options?.accent);
      const palette = [
        usedAccent.c || "var(--db-brand)",
        ...ACCENTS.filter((a) => a.c && a.id !== usedAccent.id).map((a) => a.c),
      ];
      const items = [
        { metric, series, summary, color: palette[0] },
        ...extraIds.map((id, i) => ({
          metric: api?.getMetric ? api.getMetric(id) : null,
          series: api?.getSeries ? api.getSeries(id, widget.options?.granularity) : [],
          summary: api?.getSummary ? api.getSummary(id) : {},
          color: palette[(i + 1) % palette.length],
        })),
      ];
      return <CompareW items={items} />;
    }
    if (type === "table") return <TableW metric={metric} series={series} />;
    return null;
  };

  return (
    <div className={`wc ${editing ? "editing" : ""} ${tinted ? "tinted" : ""}`} style={tintVars}>
      <div className="wc-head">
        <div className="wc-drag">
          {editing && (
            <span className="wc-grip" aria-hidden="true">
              <IcGrip />
            </span>
          )}
          <span className="wc-titles">
            {eyebrow && <div className="wc-metric">{eyebrow}</div>}
            <div className="wc-title">{title}</div>
          </span>
        </div>
        <button ref={btnRef} type="button" className="wc-menu-btn db-desktop-only" onClick={openMenu} aria-label="Widget options">
          <IcDots />
        </button>
      </div>
      <div className="wc-body" style={accentVars}>{renderBody()}</div>

      {menuAt && createPortal(
        <div ref={popRef} className="db-pop" style={{ top: menuAt.top, left: menuAt.left }}>
          <div className="grp">Show as</div>
          <div className="db-pop-types">
            {CHART_TYPES.map((o) => (
              <button
                key={o.t}
                type="button"
                aria-pressed={type === o.t}
                onClick={() => {
                  onChangeType(o.t);
                  setMenuAt(null);
                }}
              >
                {o.ic}
                {o.label}
              </button>
            ))}
          </div>
          <div className="sep" />
          <button
            type="button"
            onClick={() => {
              onEdit();
              setMenuAt(null);
            }}
          >
            <IcEdit />
            Edit widget
          </button>
          <button
            type="button"
            onClick={() => {
              onDuplicate();
              setMenuAt(null);
            }}
          >
            <IcCopy />
            Duplicate
          </button>
          {onCopyToDashboard && (
            <button
              type="button"
              onClick={() => {
                onCopyToDashboard();
                setMenuAt(null);
              }}
            >
              <IcDashboard />
              Copy to dashboard
            </button>
          )}
          <div className="sep" />
          <button
            type="button"
            className="danger"
            onClick={() => {
              onRemove();
              setMenuAt(null);
            }}
          >
            <IcTrash />
            Remove
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

WidgetCard.propTypes = {
  widget: PropTypes.object.isRequired,
  metric: PropTypes.object,
  series: PropTypes.array,
  summary: PropTypes.object,
  loading: PropTypes.bool,
  editing: PropTypes.bool,
  api: PropTypes.object,
  onChangeType: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onCopyToDashboard: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
};
